import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

function calculateExampleCredit(rules: any, originalPrice: number, actualRevenue: number): number {
  let credit = actualRevenue;
  if (rules.cancellation_fee_type === "percentage") {
    credit = credit * (1 - (rules.cancellation_fee_value || 0) / 100);
  } else if (rules.cancellation_fee_type === "fixed") {
    credit = credit - (rules.cancellation_fee_value || 0);
  }
  if (rules.cap_at_original_price) {
    credit = Math.min(credit, originalPrice);
  }
  return Math.max(0, Math.round(credit));
}

interface SettingsStandingTimesProps {
  settings: Record<string, unknown>;
  updateSetting: (key: string, value: unknown) => void;
}

export default function SettingsStandingTimes({ settings, updateSetting }: SettingsStandingTimesProps) {
  const { t } = useTranslation("admin");

  const rules = (settings.subscription_release_rules as any) ?? {
    enabled: false,
    credit_mode: "percentage",
    credit_percentage: 80,
    eligible_booking_types: "all",
    credit_validity_days: 90,
  };

  const updateRule = (key: string, value: unknown) => {
    updateSetting("subscription_release_rules", { ...rules, [key]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-base">{t("settings.standingTimes.title")}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t("settings.standingTimes.description")}
        </p>
      </div>

      {/* Master toggle */}
      <div className="flex items-center justify-between">
        <div>
          <Label>{t("settings.standingTimes.enableRelease")}</Label>
          <p className="text-xs text-muted-foreground">{t("settings.standingTimes.enableReleaseDesc")}</p>
        </div>
        <Switch
          checked={rules.enabled ?? false}
          onCheckedChange={(v) => updateRule("enabled", v)}
        />
      </div>

      {rules.enabled && (
        <>
          {/* Credit rules */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">{t("settings.standingTimes.creditRules")}</h4>

            <div>
              <Label className="text-xs">{t("settings.standingTimes.creditCalculation")}</Label>
              <RadioGroup
                value={rules.credit_mode ?? "percentage"}
                onValueChange={(v) => updateRule("credit_mode", v)}
                className="mt-2 space-y-3"
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="percentage" id="mode-pct" className="mt-0.5" />
                  <div>
                    <Label htmlFor="mode-pct" className="text-sm font-normal">
                      {t("settings.standingTimes.percentageMode")}
                    </Label>
                    {rules.credit_mode === "percentage" && (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={rules.credit_percentage ?? 80}
                          onChange={(e) => updateRule("credit_percentage", parseInt(e.target.value) || 0)}
                          className="h-8 w-20 text-sm"
                          min={0}
                          max={100}
                        />
                        <span className="text-sm text-muted-foreground">
                          {t("settings.standingTimes.percentageOfPrice")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <RadioGroupItem value="actual_revenue" id="mode-actual" className="mt-0.5" />
                  <div>
                    <Label htmlFor="mode-actual" className="text-sm font-normal">
                      {t("settings.standingTimes.actualRevenueMode")}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.standingTimes.actualRevenueModeDesc")}
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Cap + fee options (only for actual_revenue mode) */}
            {rules.credit_mode === "actual_revenue" && (
              <div className="ml-6 mt-3 space-y-4 border-l-2 border-muted pl-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="text-sm">{t("settings.standingTimes.capAtOriginalPrice")}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.standingTimes.capAtOriginalPriceDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={rules.cap_at_original_price ?? false}
                    onCheckedChange={(v) => updateRule("cap_at_original_price", v)}
                  />
                </div>

                <div>
                  <Label className="text-xs">{t("settings.standingTimes.cancellationFee")}</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {t("settings.standingTimes.cancellationFeeDesc")}
                  </p>
                  <RadioGroup
                    value={rules.cancellation_fee_type ?? "none"}
                    onValueChange={(v) => updateRule("cancellation_fee_type", v)}
                    className="space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="none" id="fee-none" />
                      <Label htmlFor="fee-none" className="text-sm font-normal">{t("settings.standingTimes.feeNone")}</Label>
                    </div>
                    <div className="flex items-start gap-2">
                      <RadioGroupItem value="percentage" id="fee-pct" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="fee-pct" className="text-sm font-normal">{t("settings.standingTimes.feePercentage")}</Label>
                        {rules.cancellation_fee_type === "percentage" && (
                          <div className="flex items-center gap-2 mt-1">
                            <Input type="number" value={rules.cancellation_fee_value ?? 0} onChange={(e) => updateRule("cancellation_fee_value", parseInt(e.target.value) || 0)} className="h-8 w-20 text-sm" min={0} max={100} />
                            <span className="text-sm text-muted-foreground">{t("settings.standingTimes.feePercentageSuffix")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <RadioGroupItem value="fixed" id="fee-fixed" className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor="fee-fixed" className="text-sm font-normal">{t("settings.standingTimes.feeFixed")}</Label>
                        {rules.cancellation_fee_type === "fixed" && (
                          <div className="flex items-center gap-2 mt-1">
                            <Input type="number" value={rules.cancellation_fee_value ?? 0} onChange={(e) => updateRule("cancellation_fee_value", parseInt(e.target.value) || 0)} className="h-8 w-24 text-sm" min={0} />
                            <span className="text-sm text-muted-foreground">{t("settings.standingTimes.feeFixedSuffix")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                <div className="bg-muted/50 rounded-md p-3 text-xs">
                  <p className="font-medium mb-1">{t("settings.standingTimes.exampleTitle")}</p>
                  <p className="text-muted-foreground">
                    {t("settings.standingTimes.exampleText", {
                      original: 550,
                      actual: 800,
                      credit: calculateExampleCredit(rules, 550, 800),
                    })}
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">{t("settings.standingTimes.eligibleTypes")}</Label>
              <RadioGroup
                value={rules.eligible_booking_types ?? "all"}
                onValueChange={(v) => updateRule("eligible_booking_types", v)}
                className="mt-2 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="all" id="type-all" />
                  <Label htmlFor="type-all" className="text-sm font-normal">
                    {t("settings.standingTimes.allBookingTypes")}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="revenue_only" id="type-revenue" />
                  <Label htmlFor="type-revenue" className="text-sm font-normal">
                    {t("settings.standingTimes.revenueOnly")}
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Credit validity */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">{t("settings.standingTimes.creditValidity")}</h4>
            <div>
              <Label className="text-xs">{t("settings.standingTimes.releaseCreditsValidity")}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={rules.credit_validity_days ?? 90}
                  onChange={(e) => updateRule("credit_validity_days", parseInt(e.target.value) || 0)}
                  className="h-8 w-20 text-sm"
                  min={0}
                />
                <span className="text-sm text-muted-foreground">
                  {t("settings.standingTimes.daysZeroNeverExpires")}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* General credit validity */}
      <div className="border-t pt-4">
        <h4 className="font-medium text-sm">{t("settings.standingTimes.generalCreditValidity")}</h4>
        <p className="text-xs text-muted-foreground mb-3">
          {t("settings.standingTimes.generalCreditValidityDesc")}
        </p>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={(settings.default_credit_validity_days as number) ?? 0}
            onChange={(e) => updateSetting("default_credit_validity_days", parseInt(e.target.value) || 0)}
            className="h-8 w-20 text-sm"
            min={0}
          />
          <span className="text-sm text-muted-foreground">
            {t("settings.standingTimes.daysZeroNeverExpires")}
          </span>
        </div>
      </div>
    </div>
  );
}
